import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faCheckCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./CreateTeam.css";
import { drivers, constructors, TOTAL_BUDGET } from '../data/teamData';
import { useAuth } from '../auth';
import { fantasyTeamService } from '../services/firestore';

const constructorBackgrounds = {
  'red_bull': '/images/redbull.png',
  'ferrari': '/images/ferrari.png',
  'mercedes': '/images/mercedes.png',
  'mclaren': '/images/mclaren.png',
  'aston_martin': '/images/astonmartin.png',
  'alpine': '/images/alpine.png',
  'williams': '/images/williams.png',
  'rb': '/images/visacashapp.png',
  'kick_sauber': '/images/kicksauber.png',
  'haas': '/images/haas.png'
};

const teamColors = {
  'red_bull': '#0600EF',
  'ferrari': '#E8002D',
  'mercedes': '#00D2BE',
  'mclaren': '#FF8700',
  'aston_martin': '#006F62',
  'alpine': '#0090FF',
  'williams': '#00A0DC',
  'rb': '#6692FF',
  'kick_sauber': '#52E252',
  'haas': '#B6BABD'
};

const CreateTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedConstructor, setSelectedConstructor] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [teamColor, setTeamColor] = useState('#E10600');
  const [backgroundImage, setBackgroundImage] = useState('');

  useEffect(() => {
    const checkTeam = async () => {
      if (!user?.uid) {
        navigate('/login');
        return;
      }

      try {
        // Set team branding
        const selectedTeam = user.firestoreUser?.favoriteTeam;
        if (selectedTeam) {
          setBackgroundImage(constructorBackgrounds[selectedTeam] || '');
          setTeamColor(teamColors[selectedTeam] || '#E10600');
        }

        // Check if user already has a team
        const existingTeam = await fantasyTeamService.getTeam(user.uid);
        if (existingTeam) {
          navigate('/dashboard');
          return;
        }
      } catch (error) {
        console.error('Error checking team:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkTeam();
  }, [user, navigate]);

  const calculateRemainingBudget = () => {
    const driversCost = selectedDrivers.reduce((total, driver) => {
      return total + driver.price;
    }, 0);

    const constructorCost = selectedConstructor ? selectedConstructor.price : 0;
    return (TOTAL_BUDGET - driversCost - constructorCost).toFixed(1);
  };

  const handleDriverSelect = (driver) => {
    if (selectedDrivers.find(d => d.id === driver.id)) {
      setSelectedDrivers(selectedDrivers.filter((d) => d.id !== driver.id));
    } else if (selectedDrivers.length < 4) {
      const newBudget = parseFloat(calculateRemainingBudget()) - driver.price;
      if (newBudget >= 0) {
        setSelectedDrivers([...selectedDrivers, driver]);
        setError("");
      } else {
        setError("Not enough budget!");
      }
    } else {
      setError("You can only select 4 drivers");
    }
  };

  const handleConstructorSelect = (constructor) => {
    setSelectedConstructor(constructor);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (selectedDrivers.length !== 4) {
      setError("Please select exactly 4 drivers");
      return;
    }

    if (!selectedConstructor) {
      setError("Please select a constructor");
      return;
    }

    const remainingBudget = parseFloat(calculateRemainingBudget());
    if (remainingBudget < 0) {
      setError("Team exceeds budget limit");
      return;
    }

    try {
      const teamData = {
        driver_1: selectedDrivers[0].id,
        driver_2: selectedDrivers[1].id,
        driver_3: selectedDrivers[2].id,
        driver_4: selectedDrivers[3].id,
        constructor: selectedConstructor.id,
        budgetRemaining: remainingBudget,
      };

      // Validate before creating
      const validation = fantasyTeamService.validateTeam(teamData);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      // Create team in Firestore
      await fantasyTeamService.createTeam(user.uid, teamData);
      
      console.log('✅ Team created successfully');
      navigate("/dashboard");
    } catch (error) {
      console.error("Error creating team:", error);
      setError(error.message || "Failed to create team");
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div 
      className="create-team-container"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        '--team-color': teamColor
      }}
    >
      {/* Navigation - Match Dashboard */}
      <nav className="create-team-nav">
        <button className="back-btn" onClick={handleBack} style={{ borderColor: teamColor, color: teamColor }}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
        <h1 className="page-title">Create Your Team</h1>
        <div className="nav-spacer"></div>
      </nav>

      <form onSubmit={handleSubmit} className="team-form">
        {/* Budget Info */}
        <div className="budget-bar">
          <div className="budget-card">
            <span className="budget-label">Total Budget</span>
            <span className="budget-value" style={{ color: teamColor }}>${TOTAL_BUDGET}M</span>
          </div>
          <div className="budget-divider"></div>
          <div className="budget-card">
            <span className="budget-label">Remaining</span>
            <span 
              className="budget-value"
              style={{ color: parseFloat(calculateRemainingBudget()) < 0 ? '#ff4444' : teamColor }}
            >
              ${calculateRemainingBudget()}M
            </span>
          </div>
          <div className="budget-divider"></div>
          <div className="budget-card">
            <span className="budget-label">Selected</span>
            <span className="budget-value" style={{ color: teamColor }}>
              {selectedDrivers.length}/4
            </span>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <FontAwesomeIcon icon={faTimes} />
            <span>{error}</span>
          </div>
        )}

        {/* Drivers Section */}
        <div className="form-section">
          <div className="section-header">
            <h2>Select Drivers</h2>
            <span className="section-subtitle">Choose exactly 4 drivers for your team</span>
          </div>
          <div className="drivers-grid">
            {drivers.map((driver) => {
              const isSelected = selectedDrivers.find(d => d.id === driver.id);
              return (
                <div
                  key={driver.id}
                  className={`driver-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleDriverSelect(driver)}
                  style={{
                    borderColor: isSelected ? teamColor : 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {isSelected && (
                    <div className="selected-badge" style={{ backgroundColor: teamColor }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                  )}
                  <div className="driver-info">
                    <h3>{driver.name}</h3>
                    <p className="driver-team">{driver.team}</p>
                    <div className="driver-price">
                      <span className="price-label">Price</span>
                      <span className="price-value" style={{ color: teamColor }}>${driver.price}M</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Constructor Section */}
        <div className="form-section">
          <div className="section-header">
            <h2>Select Constructor</h2>
            <span className="section-subtitle">Choose your team's constructor</span>
          </div>
          <div className="constructors-grid">
            {constructors.map((constructor) => {
              const isSelected = selectedConstructor?.id === constructor.id;
              return (
                <div
                  key={constructor.id}
                  className={`constructor-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleConstructorSelect(constructor)}
                  style={{
                    borderColor: isSelected ? teamColor : 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {isSelected && (
                    <div className="selected-badge" style={{ backgroundColor: teamColor }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                  )}
                  <div className="constructor-info">
                    <h3>{constructor.name}</h3>
                    <div className="constructor-price">
                      <span className="price-label">Price</span>
                      <span className="price-value" style={{ color: teamColor }}>${constructor.price}M</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          className="submit-btn"
          style={{ backgroundColor: teamColor }}
        >
          <FontAwesomeIcon icon={faSave} />
          <span>Create Team</span>
        </button>
      </form>
    </div>
  );
};

export default CreateTeam;
